import { useState } from 'react';
import { trackAction } from '~portal/lib';

export type UseChatInputProps = {
  onSubmit?: (val: string) => void;
  disabled?: boolean;
  /** Whether the conversation already has messages — drives the placeholder. */
  hasMessages?: boolean;
};

export function useChatInput({ onSubmit, disabled, hasMessages }: UseChatInputProps) {
  const [input, setInput] = useState('');

  const handleSubmit = () => {
    // Trimmed at the boundary: an empty or whitespace-only composer submits nothing at all
    // (Enter and the send button alike), so no conversation is ever created for a blank prompt.
    const text = input.trim();
    if (!text || disabled) return;
    // Length and position only — the prompt itself is content and never leaves as a dimension.
    trackAction('prompt_sent', { length: text.length, first_turn: !hasMessages });
    onSubmit?.(text);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isSubmitDisabled = disabled || !input.trim();
  // Charlie composer copy: a fresh thread invites a question, an in-flight turn
  // says the agent is busy, and an established thread asks for more context.
  const placeholder = disabled
    ? 'Having a think…'
    : hasMessages
      ? 'Give us more context…'
      : 'Ask us a question…';

  return {
    input,
    setInput,
    handleSubmit,
    handleKeyDown,
    isSubmitDisabled,
    placeholder,
  };
}
